import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../services/apiService';

const GERMAN_STATE_NAMES = {
  'baden-württemberg': 'Baden-Württemberg',
  'baden-wurttemberg': 'Baden-Württemberg',
  bavaria: 'Bayern',
  bayern: 'Bayern',
  berlin: 'Berlin',
  brandenburg: 'Brandenburg',
  bremen: 'Bremen',
  hamburg: 'Hamburg',
  hesse: 'Hessen',
  hessen: 'Hessen',
  'lower saxony': 'Niedersachsen',
  niedersachsen: 'Niedersachsen',
  'mecklenburg-vorpommern': 'Mecklenburg-Vorpommern',
  'north rhine-westphalia': 'Nordrhein-Westfalen',
  'nordrhein-westfalen': 'Nordrhein-Westfalen',
  'rhineland-palatinate': 'Rheinland-Pfalz',
  'rheinland-pfalz': 'Rheinland-Pfalz',
  saarland: 'Saarland',
  saxony: 'Sachsen',
  sachsen: 'Sachsen',
  'saxony-anhalt': 'Sachsen-Anhalt',
  'sachsen-anhalt': 'Sachsen-Anhalt',
  'schleswig-holstein': 'Schleswig-Holstein',
  thuringia: 'Thüringen',
  thüringen: 'Thüringen',
};

const normalizeGermanState = stateName =>
  GERMAN_STATE_NAMES[stateName?.trim().toLocaleLowerCase('de-DE')] || '';

const getAddressComponent = (addressComponents, type) =>
  addressComponents?.find(component => component.types.includes(type))?.long_name || '';

const EMPTY_PROSPECT = {
  company: '',
  companyAddon: '',
  salutation: '',
  firstName: '',
  lastName: '',
  street: '',
  postalCode: '',
  city: '',
  country: 'Germany',
  state: '',
  email: '',
  phone: '',
  mobile: '',
  website: '',
  notes: '',
  status: 'neu',
  customerCategoryId: '',
  customerGroupId: '',
  customerOriginId: '',
  responsibleUser: '',
};

const EMPTY_CONTACT = {
  contactFirstName: '',
  contactLastName: '',
  contactEmail: '',
  contactPhone: '',
  contactMobile: '',
  contactFunction: '',
  crmRole: '',
  isMainContact: false,
  notes: '',
  birthday: '',
};

const ProspectForm = () => {
  const { prospectId } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY_PROSPECT);
  const [contacts, setContacts] = useState([]);
  const [contactForm, setContactForm] = useState(EMPTY_CONTACT);
  const [editingContactId, setEditingContactId] = useState(null);
  const [isAddingContact, setIsAddingContact] = useState(false);
  const [activeTab, setActiveTab] = useState('personal');
  const [lookups, setLookups] = useState({ origins: [], categories: [], groups: [], users: [] });
  const [loading, setLoading] = useState(!!prospectId);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const companyInputRef = useRef(null);
  const postalCodeInputRef = useRef(null);
  const postalCodeWasEditedRef = useRef(false);
  const [postalCodeLookup, setPostalCodeLookup] = useState({ status: 'idle', message: '' });

  useEffect(() => {
    Promise.all([
      api.get('/customerOrigins'),
      api.get('/customerCategories'),
      api.get('/customerGroups'),
      api.get('/users'),
    ]).then(([origins, categories, groups, users]) => {
      setLookups({
        origins: origins.data || [],
        categories: categories.data || [],
        groups: groups.data || [],
        users: Array.isArray(users.data?.rows) ? users.data.rows : users.data || [],
      });
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!prospectId) return;
    setLoading(true);
    api.get(`/prospects/${prospectId}`)
      .then(response => {
        const prospect = response.data;
        setForm({
          ...EMPTY_PROSPECT,
          ...prospect,
          customerCategoryId: prospect.customerCategoryId || '',
          customerGroupId: prospect.customerGroupId || '',
          customerOriginId: prospect.customerOriginId || '',
          responsibleUser: prospect.responsibleUser || '',
        });
        setContacts(prospect.contacts || []);
      })
      .catch(requestError => setError(requestError.response?.data?.error || 'Interessent konnte nicht geladen werden.'))
      .finally(() => setLoading(false));
  }, [prospectId]);

  useEffect(() => {
    if (loading) return undefined;

    let autocomplete;
    let listener;
    let retryId;

    const initializeAutocomplete = () => {
      if (!window.google?.maps?.places || !companyInputRef.current || autocomplete) {
        return Boolean(autocomplete);
      }

      autocomplete = new window.google.maps.places.Autocomplete(
        companyInputRef.current,
        { types: ['establishment'], componentRestrictions: { country: 'de' } }
      );
      autocomplete.setFields(['name', 'address_components', 'formatted_phone_number', 'website']);
      listener = autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        const getComponent = types =>
          place.address_components?.find(component =>
            types.every(type => component.types.includes(type))
          )?.long_name || '';
        const state = normalizeGermanState(getComponent(['administrative_area_level_1']));

        postalCodeWasEditedRef.current = false;
        setForm(current => ({
          ...current,
          company: place.name || current.company,
          street: `${getComponent(['route'])} ${getComponent(['street_number'])}`.trim(),
          postalCode: getComponent(['postal_code']),
          city: getComponent(['locality']) || getComponent(['postal_town']),
          state: state || current.state,
          country: 'Germany',
          phone: place.formatted_phone_number || current.phone,
          website: place.website || current.website,
        }));
        setPostalCodeLookup({ status: 'idle', message: '' });
      });

      if (retryId) window.clearInterval(retryId);
      return true;
    };

    if (!initializeAutocomplete()) {
      retryId = window.setInterval(initializeAutocomplete, 250);
    }

    return () => {
      if (retryId) window.clearInterval(retryId);
      if (listener && window.google?.maps?.event) {
        window.google.maps.event.removeListener(listener);
      }
    };
  }, [loading]);

  useEffect(() => {
    if (loading || activeTab !== 'personal') return undefined;

    let autocomplete;
    let listener;
    let retryId;

    const initializeAutocomplete = () => {
      if (!window.google?.maps?.places || !postalCodeInputRef.current || autocomplete) {
        return Boolean(autocomplete);
      }

      autocomplete = new window.google.maps.places.Autocomplete(
        postalCodeInputRef.current,
        {
          types: ['(regions)'],
          componentRestrictions: { country: 'de' },
        }
      );
      autocomplete.setFields(['address_components']);
      listener = autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        const components = place.address_components;
        const postalCode = getAddressComponent(components, 'postal_code');

        if (!postalCode) {
          setPostalCodeLookup({
            status: 'error',
            message: 'Bitte einen Vorschlag mit Postleitzahl auswählen.',
          });
          return;
        }

        const city =
          getAddressComponent(components, 'locality') ||
          getAddressComponent(components, 'postal_town') ||
          getAddressComponent(components, 'administrative_area_level_3');
        const state = normalizeGermanState(
          getAddressComponent(components, 'administrative_area_level_1')
        );

        postalCodeWasEditedRef.current = false;
        setForm(current => ({
          ...current,
          postalCode,
          city: city || current.city,
          state: state || current.state,
          country: 'Germany',
        }));
        setPostalCodeLookup({
          status: 'success',
          message: `${[city, state].filter(Boolean).join(', ')} automatisch übernommen.`,
        });
      });

      if (retryId) window.clearInterval(retryId);
      return true;
    };

    if (!initializeAutocomplete()) {
      retryId = window.setInterval(initializeAutocomplete, 250);
    }

    return () => {
      if (retryId) window.clearInterval(retryId);
      if (listener && window.google?.maps?.event) {
        window.google.maps.event.removeListener(listener);
      }
    };
  }, [activeTab, loading]);

  useEffect(() => {
    const postalCode = form.postalCode.trim();
    const isGermany = !form.country || form.country === 'Germany';

    if (!postalCodeWasEditedRef.current || !isGermany || !/^\d{5}$/.test(postalCode)) {
      if (postalCodeWasEditedRef.current) {
        setPostalCodeLookup({ status: 'idle', message: '' });
      }
      return undefined;
    }

    let cancelled = false;
    const timeoutId = window.setTimeout(() => {
      if (!window.google?.maps?.Geocoder) {
        setPostalCodeLookup({
          status: 'error',
          message: 'PLZ konnte nicht automatisch geprüft werden. Ort und Bundesland bitte manuell eintragen.',
        });
        return;
      }

      setPostalCodeLookup({ status: 'loading', message: 'Ort und Bundesland werden ermittelt …' });
      const geocoder = new window.google.maps.Geocoder();

      geocoder.geocode(
        {
          address: `${postalCode} Deutschland`,
          componentRestrictions: { country: 'DE', postalCode },
        },
        (results, status) => {
          if (cancelled || !postalCodeWasEditedRef.current) return;

          if (status !== 'OK' || !results?.length) {
            setPostalCodeLookup({
              status: 'error',
              message: 'Keine eindeutige Zuordnung gefunden. Ort und Bundesland bitte manuell eintragen.',
            });
            return;
          }

          const result = results.find(item =>
            getAddressComponent(item.address_components, 'postal_code') === postalCode
          ) || results[0];
          const components = result.address_components;
          const city =
            getAddressComponent(components, 'locality') ||
            getAddressComponent(components, 'postal_town') ||
            getAddressComponent(components, 'administrative_area_level_3');
          const state = normalizeGermanState(
            getAddressComponent(components, 'administrative_area_level_1')
          );

          if (!city && !state) {
            setPostalCodeLookup({
              status: 'error',
              message: 'Keine eindeutige Zuordnung gefunden. Ort und Bundesland bitte manuell eintragen.',
            });
            return;
          }

          setForm(current => {
            if (current.postalCode.trim() !== postalCode) return current;
            return {
              ...current,
              city: city || current.city,
              state: state || current.state,
              country: 'Germany',
            };
          });
          postalCodeWasEditedRef.current = false;
          setPostalCodeLookup({
            status: 'success',
            message: `${[city, state].filter(Boolean).join(', ')} automatisch übernommen.`,
          });
        }
      );
    }, 400);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [form.postalCode, form.country]);

  const change = event => {
    const { name, value } = event.target;
    setForm(current => ({ ...current, [name]: value }));
  };

  const changePostalCode = event => {
    const isGermany = !form.country || form.country === 'Germany';
    const value = isGermany
      ? event.target.value.replace(/\D/g, '').slice(0, 5)
      : event.target.value;

    postalCodeWasEditedRef.current = true;
    setForm(current => ({ ...current, postalCode: value }));
  };

  const save = async event => {
    event.preventDefault();
    setSaving(true);
    setError('');
    try {
      const response = prospectId
        ? await api.put(`/prospects/${prospectId}`, form)
        : await api.post('/prospects', form);
      if (!prospectId) {
        navigate(`/prospects/${response.data.id}/edit`, { replace: true });
      } else {
        setForm(current => ({ ...current, ...response.data }));
        alert('Interessent gespeichert.');
      }
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Interessent konnte nicht gespeichert werden.');
    } finally {
      setSaving(false);
    }
  };

  const changeContact = event => {
    const { name, value, type, checked } = event.target;
    setContactForm(current => ({
      ...current,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const saveContact = async () => {
    if (!contactForm.contactFirstName || !contactForm.contactLastName || !contactForm.contactEmail) {
      alert('Vorname, Nachname und E-Mail sind für Ansprechpartner erforderlich.');
      return;
    }
    try {
      const response = editingContactId
        ? await api.put(`/prospects/${prospectId}/contacts/${editingContactId}`, contactForm)
        : await api.post(`/prospects/${prospectId}/contacts`, contactForm);
      setContacts(current => {
        const normalized = response.data.isMainContact
          ? current.map(contact => ({ ...contact, isMainContact: false }))
          : current;
        return editingContactId
          ? normalized.map(contact => contact.id === editingContactId ? response.data : contact)
          : [...normalized, response.data];
      });
      setContactForm(EMPTY_CONTACT);
      setEditingContactId(null);
      setIsAddingContact(false);
    } catch (requestError) {
      alert(requestError.response?.data?.error || 'Ansprechpartner konnte nicht gespeichert werden.');
    }
  };

  const editContact = contact => {
    setEditingContactId(contact.id);
    setContactForm({ ...EMPTY_CONTACT, ...contact, birthday: contact.birthday || '' });
    setIsAddingContact(true);
  };

  const cancelContact = () => {
    setEditingContactId(null);
    setContactForm(EMPTY_CONTACT);
    setIsAddingContact(false);
  };

  const startContact = () => {
    setEditingContactId(null);
    setContactForm(EMPTY_CONTACT);
    setIsAddingContact(true);
  };

  const deleteContact = async contact => {
    if (!window.confirm('Ansprechpartner wirklich löschen?')) return;
    try {
      await api.delete(`/prospects/${prospectId}/contacts/${contact.id}`);
      setContacts(current => current.filter(item => item.id !== contact.id));
    } catch (requestError) {
      alert(requestError.response?.data?.error || 'Ansprechpartner konnte nicht gelöscht werden.');
    }
  };

  if (loading) return <div>Lade Interessent …</div>;
  const converted = form.status === 'konvertiert';

  return (
    <div>
      <Helmet><title>{prospectId ? 'Interessent bearbeiten' : 'Interessent anlegen'}</title></Helmet>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2>{prospectId ? `Interessent I-${prospectId}` : 'Interessent anlegen'}</h2>
        <div>
          <button type="button" onClick={() => navigate('/prospects')} style={secondaryButton}>Zur Liste</button>
          {prospectId && !converted && (
            <button
              type="button"
              onClick={() => navigate(`/prospects/${prospectId}/convert`)}
              style={convertButton}
            >
              In Kunden umwandeln
            </button>
          )}
          {converted && form.convertedCustomerId && (
            <button
              type="button"
              onClick={() => navigate(`/customerform/${form.convertedCustomerId}`)}
              style={convertButton}
            >
              Zum Kunden
            </button>
          )}
        </div>
      </div>

      {error && <div style={{ color: '#a00', marginBottom: 12 }}>{error}</div>}
      {converted && (
        <div style={{ background: '#e7f6ea', border: '1px solid #91cf9c', padding: 10, marginBottom: 14 }}>
          Dieser Interessent wurde bereits in einen Kunden umgewandelt.
        </div>
      )}

      <form onSubmit={save}>
        <fieldset disabled={converted || saving} style={formSectionStyle}>
          <div className="company-input">
            <input
              name="company"
              ref={companyInputRef}
              value={form.company || ''}
              onChange={change}
              placeholder="Firma*"
              required
            />
          </div>

          <div className="company-addon">
            <input
              name="companyAddon"
              value={form.companyAddon || ''}
              onChange={change}
              placeholder="Firmenzusatz"
            />
          </div>
        </fieldset>

        <div className="tabs">
          <button
            type="button"
            className={activeTab === 'personal' ? 'active' : ''}
            onClick={() => setActiveTab('personal')}
          >
            Kontakte und Adressen
          </button>
          {prospectId && (
            <button
              type="button"
              className={activeTab === 'contact' ? 'active' : ''}
              onClick={() => setActiveTab('contact')}
            >
              Ansprechpartner
            </button>
          )}
        </div>

        {activeTab === 'personal' && (
          <fieldset disabled={converted || saving} style={formSectionStyle}>
            <div className="form-row">
              <div className="col">
                <input name="street" value={form.street || ''} onChange={change} placeholder="Straße und Nr." />
                <input
                  name="postalCode"
                  ref={postalCodeInputRef}
                  value={form.postalCode || ''}
                  onChange={changePostalCode}
                  placeholder="PLZ"
                  inputMode={form.country === 'Germany' ? 'numeric' : 'text'}
                  autoComplete="postal-code"
                  maxLength={form.country === 'Germany' ? 5 : undefined}
                />
                {postalCodeLookup.message && (
                  <small
                    role="status"
                    style={{
                      color: postalCodeLookup.status === 'error' ? '#a33' : '#466',
                      margin: '-5px 0 10px',
                    }}
                  >
                    {postalCodeLookup.message}
                  </small>
                )}
                <input name="city" value={form.city || ''} onChange={change} placeholder="Ort" />
                <input name="state" value={form.state || ''} onChange={change} placeholder="Bundesland" />
                <input name="country" value={form.country || ''} onChange={change} placeholder="Land" />
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input
                    name="website"
                    value={form.website || ''}
                    onChange={change}
                    placeholder="Webseite"
                    style={{ flex: 1 }}
                  />
                  {form.website && (
                    <button
                      type="button"
                      title="Webseite öffnen"
                      onClick={() => {
                        let url = form.website.trim();
                        if (url && !/^https?:\/\//i.test(url)) url = `https://${url}`;
                        window.open(url, '_blank', 'noopener,noreferrer');
                      }}
                      style={websiteButton}
                    >
                      🌐
                    </button>
                  )}
                </div>
              </div>

              <div className="col">
                <select name="salutation" value={form.salutation || ''} onChange={change} style={selectStyle}>
                  <option value="">Keine Anrede</option>
                  <option value="Herr">Herr</option>
                  <option value="Frau">Frau</option>
                </select>
                <input name="firstName" value={form.firstName || ''} onChange={change} placeholder="Vorname" />
                <input name="lastName" value={form.lastName || ''} onChange={change} placeholder="Nachname" />
                <input name="phone" value={form.phone || ''} onChange={change} placeholder="Telefon" />
                <input name="mobile" value={form.mobile || ''} onChange={change} placeholder="Mobil (+49177...)" />
              </div>

              <div className="col">
                <input name="email" value={form.email || ''} onChange={change} placeholder="E-Mail-Adresse" type="email" />
                <select name="customerCategoryId" value={form.customerCategoryId || ''} onChange={change} style={selectStyle}>
                  <option value="">Interessentenkategorie wählen</option>
                  {lookups.categories.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
                </select>
                <select name="customerGroupId" value={form.customerGroupId || ''} onChange={change} style={selectStyle}>
                  <option value="">Interessentengruppe wählen</option>
                  {lookups.groups.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
                </select>
                <select name="customerOriginId" value={form.customerOriginId || ''} onChange={change} style={selectStyle}>
                  <option value="">Herkunft wählen</option>
                  {lookups.origins.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
                </select>
                <select name="status" value={form.status || 'neu'} onChange={change} style={selectStyle}>
                  <option value="neu">Neu</option>
                  <option value="kontaktiert">Kontaktiert</option>
                  <option value="qualifiziert">Qualifiziert</option>
                  <option value="verloren">Verloren</option>
                  {converted && <option value="konvertiert">Konvertiert</option>}
                </select>
                <select name="responsibleUser" value={form.responsibleUser || ''} onChange={change} style={selectStyle}>
                  <option value="">Verantwortlichen wählen</option>
                  {lookups.users.map(user => (
                    <option key={user.id} value={user.id}>
                      {`${user.firstName || ''} ${user.lastName || ''}`.trim() || `#${user.id}`}
                    </option>
                  ))}
                </select>
                <label htmlFor="prospectNotes" style={{ textAlign: 'left', marginBottom: 4 }}>
                  Interne Interessentennotiz
                </label>
                <textarea
                  id="prospectNotes"
                  name="notes"
                  value={form.notes || ''}
                  onChange={change}
                  rows={6}
                  placeholder="Notizen zum Interessenten …"
                  style={notesStyle}
                />
              </div>
            </div>
          </fieldset>
        )}

        {activeTab === 'contact' && prospectId && (
          <fieldset disabled={converted} style={formSectionStyle}>
            <h3>Liste der Ansprechpartner</h3>
            <table>
              <thead>
                <tr>
                  <th>Vorname</th>
                  <th>Nachname</th>
                  <th>E-Mail</th>
                  <th>Telefon</th>
                  <th>Mobil</th>
                  <th>Funktion</th>
                  <th>CRM-Rolle</th>
                  <th>Hauptkontakt</th>
                  <th>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {isAddingContact && (
                  <tr>
                    <td><input name="contactFirstName" value={contactForm.contactFirstName} onChange={changeContact} placeholder="Vorname*" /></td>
                    <td><input name="contactLastName" value={contactForm.contactLastName} onChange={changeContact} placeholder="Nachname*" /></td>
                    <td><input name="contactEmail" value={contactForm.contactEmail} onChange={changeContact} placeholder="E-Mail*" type="email" /></td>
                    <td><input name="contactPhone" value={contactForm.contactPhone} onChange={changeContact} placeholder="Telefon" /></td>
                    <td><input name="contactMobile" value={contactForm.contactMobile} onChange={changeContact} placeholder="Mobil" /></td>
                    <td>
                      <select
                        name="contactFunction"
                        value={contactForm.contactFunction}
                        onChange={changeContact}
                        style={contactFunctionSelect}
                      >
                        <option value="">Bitte wählen</option>
                        <option value="Besteller">Besteller</option>
                        <option value="LW-Bediener">LW-Bediener</option>
                        <option value="Rechnungsempfang">Rechnungsempfang</option>
                      </select>
                    </td>
                    <td><input name="crmRole" value={contactForm.crmRole} onChange={changeContact} placeholder="CRM-Rolle" /></td>
                    <td>
                      <label style={mainContactLabel}>
                        <input
                          type="checkbox"
                          name="isMainContact"
                          checked={contactForm.isMainContact}
                          onChange={changeContact}
                          style={checkboxStyle}
                        />
                        Ja
                      </label>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 5 }}>
                        <button type="button" onClick={saveContact} style={contactSaveButton}>save</button>
                        <button type="button" onClick={cancelContact} style={contactCancelButton}>X</button>
                      </div>
                    </td>
                  </tr>
                )}

                {contacts
                  .filter(contact => contact.id !== editingContactId)
                  .map(contact => (
                    <tr key={contact.id}>
                      <td>{contact.contactFirstName}</td>
                      <td>{contact.contactLastName}</td>
                      <td>{contact.contactEmail}</td>
                      <td>{contact.contactPhone || '-'}</td>
                      <td>{contact.contactMobile || '-'}</td>
                      <td>{contact.contactFunction || '-'}</td>
                      <td>{contact.crmRole || '-'}</td>
                      <td>{contact.isMainContact ? 'Ja' : 'Nein'}</td>
                      <td>
                        <div style={{ display: 'flex', gap: 5 }}>
                          <button type="button" onClick={() => editContact(contact)} style={contactEditButton}>edit</button>
                          <button type="button" onClick={() => deleteContact(contact)} style={contactDeleteButton}>X</button>
                        </div>
                      </td>
                    </tr>
                  ))}

                {!contacts.length && !isAddingContact && (
                  <tr><td colSpan="9">Noch keine Ansprechpartner.</td></tr>
                )}
                <tr>
                  <td colSpan="9" style={{ textAlign: 'right' }}>
                    <button type="button" onClick={startContact} disabled={isAddingContact} style={addContactButton}>+</button>
                  </td>
                </tr>
              </tbody>
            </table>
          </fieldset>
        )}

        <div>
          <button type="submit" disabled={converted || saving}>
            {saving
              ? 'Speichert …'
              : prospectId
                ? 'Interessent aktualisieren'
                : 'Interessent hinzufügen'}
          </button>
        </div>
      </form>
    </div>
  );
};

const formSectionStyle = { border: 0, padding: 0, margin: 0, minWidth: 0 };
const selectStyle = { marginLeft: 0, marginTop: 0 };
const notesStyle = {
  width: '100%',
  minHeight: 130,
  padding: 10,
  border: '1px solid #ddd',
  borderRadius: 5,
  boxSizing: 'border-box',
  resize: 'vertical',
  font: 'inherit',
};
const websiteButton = {
  background: '#002B3E',
  color: '#fff',
  border: 'none',
  borderRadius: 5,
  padding: '6px 10px',
  cursor: 'pointer',
  fontSize: 18,
  marginLeft: 4,
  marginTop: -10,
};
const secondaryButton = { background: '#3b82f6', color: '#fff', border: 0, borderRadius: 5, padding: '7px 11px', margin: '0 6px', cursor: 'pointer' };
const convertButton = { ...secondaryButton, background: '#16803b' };
const mainContactLabel = { display: 'flex', alignItems: 'center', justifyContent: 'center' };
const checkboxStyle = { width: 'auto', margin: '0 6px 0 0', padding: 0 };
const contactSaveButton = { fontSize: 15, padding: '5px 6px', background: '#28a745' };
const contactCancelButton = { ...contactSaveButton, background: '#888' };
const contactEditButton = { ...contactSaveButton, padding: '5px 10px' };
const contactDeleteButton = { ...contactSaveButton, background: '#FF4300', padding: '10px' };
const addContactButton = { fontSize: 20, padding: '10px 15px', background: '#28a745' };
const contactFunctionSelect = {
  margin: '0 0 15px',
  minWidth: 145,
  height: 38,
  padding: 10,
  fontSize: 14,
  boxSizing: 'border-box',
  verticalAlign: 'top',
};

export default ProspectForm;
